import { getCoreUtils } from "./kCoreHandler";
import { kDocTableColumn, kDocTableCell } from "./kDocBlocksCore";
import { kDocTheme } from "./kDocThemeCore";

// kDocNumberFormatCore - turns a RAW table value into the text a financial statement prints.
//
// Numbers are stored raw in the model (kDocTableCell.Value) and formatted only at output, so one
// document renders correctly in any Locale (DocumentBuilder plan, Q10b). The rules:
//   - separators come from Intl.NumberFormat for the document Locale (sv-SE: "1 234,5";
//     en-GB: "1,234.5");
//   - a fixed number of decimals per column (kDocTableColumn.Decimals), so figures align on the
//     decimal point when right-aligned;
//   - a value that ROUNDS to zero prints Theme.ZeroDisplay (an en dash by default);
//   - a negative prints with a real minus sign (U+2212), or in parentheses
//     (Theme.NegativeStyle);
//   - Theme.ThinSpaceGrouping swaps a space-like group separator for Theme.ThinSpaceChar (U+2009
//     thin space by default - see the theme for why not U+202F). A "," or "." separator is left
//     alone.
//
// Server-safe and pure. Intl formatters are cached per (Locale, Decimals).

// Intl.NumberFormat.formatToParts is lib ES2018; the house strict isolated check compiles with
// --lib ES2017,DOM (PX-23). These local structural types describe what is used, so the call is typed
// without `any` under either lib; HasParts proves the method exists at runtime.
interface kNumberFormatPart { type: string; value: string; }
interface kPartsFormatter { formatToParts: (Value: number) => kNumberFormatPart[]; }

export class kDocNumberFormatCore {

    public static readonly MinusSign: string = "−";

    private static _Formatters: { [Key: string]: Intl.NumberFormat } = {};

    /** The printed text of one cell in one column: numbers formatted, other types as stored. */
    public static FormatCell = (Cell: kDocTableCell, Column: kDocTableColumn, Locale: string,
                                Theme: kDocTheme): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Column.Type !== "number" || Cell.Value === null) { return Cell.Text; }
            return kDocNumberFormatCore.FigureText(Cell.Value, Column, Locale, Theme, Cell.Decimals);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.FormatCell");
            return "";
        }
    };

    /**
     * A table figure as EVERY emitter prints it (PDF, Word, HTML): Format, then the column's rules - SignPositive
     * puts Theme.PositiveSign before a positive figure that shows digits (not the zero dash), Suffix follows every
     * figure that shows digits ("9,3 %"). Decimals: the cell's own when >= 0, else the column's. Added 2026-09-28
     * (WP-X1, E-02 / E-03: the Word file printed "9,3" where the PDF printed "+9,3 %").
     */
    public static FigureText = (Value: number, Column: kDocTableColumn, Locale: string, Theme: kDocTheme,
                                CellDecimals: number = -1): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Decimals: number = Column.Decimals;
            if (typeof CellDecimals === "number" && CellDecimals >= 0) { Decimals = CellDecimals; }
            let Text: string = kDocNumberFormatCore.Format(Value, Decimals, Locale, Theme);
            const Digits: boolean = /[0-9]/.test(Text) === true;
            if (Column.SignPositive === true && Value > 0 && Digits === true) { Text = kDocNumberFormatCore.PositiveSignOf(Theme) + Text; }
            if (typeof Column.Suffix === "string" && Column.Suffix !== "" && Digits === true) { Text = Text + Column.Suffix; }
            return Text;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.FigureText");
            return "";
        }
    };

    /**
     * PX-139 (2026-10-11): a value in a HEADER row (a column heading, or a body row styled "header" such as a
     * second block's "Moderbolaget, MSEK | 2025 | 2024") is never formatted as a figure when it is a YEAR - an
     * integer 1900-2199 prints "2025", not "2 025,0". Any other header value prints as a figure, as before.
     */
    public static IsYear = (Value: number | null): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (typeof Value !== "number" || isFinite(Value) === false) { return false; }
            return Math.floor(Value) === Value && Value >= 1900 && Value <= 2199;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.IsYear");
            return false;
        }
    };

    /** Theme.PositiveSign, "+" when the theme has none (an older theme JSON). */
    public static PositiveSignOf = (Theme: kDocTheme): string => {
        if (getCoreUtils().KErrorMode) { return "+"; }
        try {
            if (typeof Theme.PositiveSign === "string" && Theme.PositiveSign !== "") { return Theme.PositiveSign; }
            return "+";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.PositiveSignOf");
            return "+";
        }
    };

    /** Format one raw value with a fixed number of decimals. NaN / Infinity -> "". */
    public static Format = (Value: number, Decimals: number, Locale: string, Theme: kDocTheme): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (typeof Value !== "number" || isFinite(Value) === false) { return ""; }
            const Places: number = Math.max(0, Math.min(10, Math.round(Decimals)));
            if (kDocNumberFormatCore.RoundsToZero(Value, Places) === true && Theme.ZeroDisplay !== "") {
                return Theme.ZeroDisplay;
            }
            const Digits: string = kDocNumberFormatCore.FormatMagnitude(Math.abs(Value), Places, Locale, Theme);
            if (Value >= 0 || kDocNumberFormatCore.RoundsToZero(Value, Places) === true) { return Digits; }
            if (Theme.NegativeStyle === "parentheses") { return "(" + Digits + ")"; }
            return kDocNumberFormatCore.SignOf(Theme) + Digits;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.Format");
            return "";
        }
    };

    /** The minus a negative figure starts with: Theme.NegativeSign, or U+2212 when the theme has none. */
    public static SignOf = (Theme: kDocTheme): string => {
        if (getCoreUtils().KErrorMode) { return kDocNumberFormatCore.MinusSign; }
        try {
            if (typeof Theme.NegativeSign !== "string" || Theme.NegativeSign === "") { return kDocNumberFormatCore.MinusSign; }
            return Theme.NegativeSign;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.SignOf");
            return kDocNumberFormatCore.MinusSign;
        }
    };

    /** True when Value prints as zero at this many decimals (so -0.04 at 1 decimal is zero). */
    public static RoundsToZero = (Value: number, Places: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return Math.abs(Value) < 0.5 * Math.pow(10, -Places);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.RoundsToZero");
            return false;
        }
    };

    /** The unsigned digits: Intl separators, with the thin-space rule applied to the groups. */
    public static FormatMagnitude = (Magnitude: number, Places: number, Locale: string,
                                     Theme: kDocTheme): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Formatter: object = kDocNumberFormatCore.FormatterFor(Locale, Places);
            if (kDocNumberFormatCore.HasParts(Formatter) === false) {
                // Not an error: a runtime without formatToParts (no current browser or Node) prints
                // Intl's own group separator; Magnitude is unsigned, so there is no sign to strip.
                return kDocNumberFormatCore.FormatterFor(Locale, Places).format(Magnitude);
            }
            const Parts: kNumberFormatPart[] = (Formatter as kPartsFormatter).formatToParts(Magnitude);
            let Out: string = "";
            for (const Part of Parts) {
                if (Part.type === "minusSign" || Part.type === "plusSign") { continue; }
                if (Part.type === "group" && Theme.ThinSpaceGrouping === true && Theme.ThinSpaceChar !== ""
                    && kDocNumberFormatCore.IsSpaceLike(Part.value) === true) {
                    Out = Out + Theme.ThinSpaceChar;
                    continue;
                }
                Out = Out + Part.value;
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.FormatMagnitude");
            return "";
        }
    };

    /** True when the formatter has formatToParts (lib ES2018; every current browser and Node). */
    public static HasParts = (Formatter: object): Formatter is kPartsFormatter => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return typeof (Formatter as { formatToParts?: unknown }).formatToParts === "function";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.HasParts");
            return false;
        }
    };

    /** A cached Intl.NumberFormat with exactly Places decimals and grouping on. */
    public static FormatterFor = (Locale: string, Places: number): Intl.NumberFormat => {
        if (getCoreUtils().KErrorMode) { return new Intl.NumberFormat("en-GB"); }
        try {
            const Key: string = Locale + "|" + String(Places);
            const Cached: Intl.NumberFormat | undefined = kDocNumberFormatCore._Formatters[Key];
            if (Cached !== undefined) { return Cached; }
            const Created: Intl.NumberFormat = new Intl.NumberFormat(kDocNumberFormatCore.UsableLocale(Locale), {
                minimumFractionDigits: Places, maximumFractionDigits: Places, useGrouping: true
            });
            kDocNumberFormatCore._Formatters[Key] = Created;
            return Created;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.FormatterFor");
            return new Intl.NumberFormat("en-GB");
        }
    };

    /**
     * The locale Intl gets: Locale itself, or "en-US" when the tag is empty, malformed or unknown.
     * A bad locale on one document must not stop the whole output: the figure still prints, only
     * with en-US separators (moved here from kDocxNumberText when the two formatters were merged).
     */
    public static UsableLocale = (Locale: string): string => {
        if (getCoreUtils().KErrorMode) { return "en-US"; }
        try {
            if (typeof Locale !== "string" || Locale.trim() === "") { return "en-US"; }
            if (kDocNumberFormatCore.LocaleWorks(Locale) === false) { return "en-US"; }
            return Locale;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.UsableLocale");
            return "en-US";
        }
    };

    /**
     * True when Intl knows the tag.
     * DOCUMENTED EXCEPTION: a pure probe; its only failure IS the answer (see below), and every call
     * site (UsableLocale) is guarded.
     */
    public static LocaleWorks = (Locale: string): boolean => {
        try {
            return Intl.NumberFormat.supportedLocalesOf([Locale]).length > 0;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a malformed BCP-47 tag makes supportedLocalesOf THROW a
            // RangeError; "this tag does not work" is exactly the answer, and the caller falls back
            // to en-US grouping, so the figure is still printed correctly.
            return false;
        }
    };

    /** A group separator made of whitespace (space, NBSP, narrow NBSP, thin space). */
    public static IsSpaceLike = (Separator: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return /^[\s   ]+$/.test(Separator);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocNumberFormatCore.IsSpaceLike");
            return false;
        }
    };
}
